/**
 * The MIT License (MIT)
 * Copyright (c) 2019 Akbar Basha
 */
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { ALL_MONTHS_MAP } from "src/constants/string-res-map";
import { useLocaleContext } from "src/context/locale-context";
import { isDateDisabledInCalendar, isDateWithinRange, isSameDate } from "src/utils/date-utils";
import "./ScrollCalendar.css";

const ScrollCalendar = (props) => {
  const { directFlightDates = [] } = props;
  const {
    isRangeCalendar = false,
    minDate = dayjs(),
    maxDate = dayjs().add(12, "M"),
    monthFormat = "MMMM",
    yearFormat = "YYYY",
    enableYearTitle = true,
    enableMonthTitle = true,
    isSelectingEndDate = false,
  } = props;

  const [isInit, setIsInit] = useState(true);

  const onPickDateHandler = (event, value) => {
    event && event.preventDefault();
    if (isSelectingEndDate) {
      props.onSelectEndDate(value);
    } else {
      props.onSelectStartDate(value);
    }
  };

  useEffect(() => {
    if (!isInit) return;
    const element = document.getElementById(
      dayjs(props.startDate, "DD/MMM/YYYY").format("MMMM-YYYY")
    );
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: "instant", block: "center" });
      }, 1);
    }
    setIsInit(false);
  }, [isInit, props.startDate]);

  return (
    <MonthsInRange
      minDate={minDate}
      maxDate={maxDate}
      isRangeCalendar={isRangeCalendar}
      selectedStartDate={props.startDate}
      selectedEndDate={props.endDate}
      onPickDate={onPickDateHandler}
      className={`${props.className ? props.className : ""} mobile-datepicker`}
      yearFormat={yearFormat}
      monthFormat={monthFormat}
      enableYearTitle={enableYearTitle}
      enableMonthTitle={enableMonthTitle}
      directFlightDates={directFlightDates}
    />
  );
};

export default ScrollCalendar;

export const MonthsInRange = (props) => {
  const { minDate, maxDate, directFlightDates } = props;
  const totalMonths = Math.round(maxDate.diff(minDate, "months", true)) + 1;
  let timePeriod = dayjs(minDate, "DD/MMM/YYYY");
  const elements = [];
  for (let i = 0; i < totalMonths; i++) {
    elements.push(<MonthCard key={i} timePeriod={timePeriod} {...props} directFlightDates={directFlightDates} />);
    timePeriod = timePeriod.add(1, "M");
  }
  return <div className={props.className}>{elements}</div>;
};

export const MonthCard = (props) => {
  const { timePeriod, directFlightDates } = props;
  return (
    <section className="month" id={timePeriod.format("MMMM-YYYY")}> 
      <MonthHeader timePeriod={timePeriod} {...props} />
      <DaysInMonth timePeriod={timePeriod} {...props} directFlightDates={directFlightDates} />
    </section>
  );
};

export const MonthHeader = (props) => {
  const { getStringRes } = useLocaleContext();
  const allMonths = ALL_MONTHS_MAP.map((m) => getStringRes(m));

  const month = allMonths[props.timePeriod.month()];
  const year = props.timePeriod.format(props.yearFormat);
  return (
    <p className="month-title">
      {props.enableYearTitle ? <span>{year}</span> : null}
      {props.enableMonthTitle ? month : null}
    </p>
  );
};

export const SingleDay = ({
  isActive,
  isToday,
  isRange,
  handleClick,
  currentValue,
  isDisabled,
  i,
  directFlightDates = [],
}) => {
  const className = [];
  if (isActive) className.push("active");
  if (isDisabled) className.push("disabled");
  if (isToday) className.push("today");
  if (isRange) className.push("range");
  const dateStr = currentValue.format("YYYY-MM-DD");
  if (directFlightDates.includes(dateStr)) className.push("direct-flight");

  return (
    <li className={className.join(" ")} key={i} onClick={(e) => handleClick(e, currentValue)}>
      <span>{currentValue.date()}</span>
    </li>
  );
};

export const DaysInMonth = ({
  isRangeCalendar,
  timePeriod,
  selectedStartDate,
  selectedEndDate,
  onPickDate,
  minDate,
  maxDate,
  directFlightDates = [],
}) => {
  const daysInMonth = timePeriod.daysInMonth();
  const dayOne = timePeriod.startOf("month");
  //our weekdat starts visually on Monday & handle day starting on Sunday (day=0)
  const monthStartWeekdayOffset = dayOne.day() > 0 ? dayOne.day() - 1 : 6;

  const compareDates = (calendarDay) => {
    let isSameAsStart, isSameAsEnd;
    if (selectedStartDate) {
      isSameAsStart = isSameDate(calendarDay, selectedStartDate);
    }
    if (isRangeCalendar && selectedEndDate) {
      isSameAsEnd = isSameDate(calendarDay, selectedEndDate);
    }
    return isSameAsStart || isSameAsEnd;
  };

  const renderDaysInMonth = () => {
    let elements = [];
    let calendarDay = dayjs(dayOne, "DD/MMM/YYYY");
    for (let i = 1; i <= daysInMonth; i++) {
      elements.push(
        <SingleDay
          isToday={isSameDate(dayjs(), calendarDay)}
          isRange={
            isRangeCalendar &&
            isDateWithinRange(selectedStartDate, calendarDay, selectedEndDate)
          }
          isActive={compareDates(calendarDay)}
          isDisabled={isDateDisabledInCalendar(minDate, calendarDay, maxDate)}
          handleClick={onPickDate}
          currentValue={calendarDay}
          key={i}
          directFlightDates={directFlightDates}
        />
      );
      calendarDay = calendarDay.add(1, "days");
    }
    return elements;
  };

  const renderCalendarShift = () => {
    let elements = [];
    for (let i = 0; i < monthStartWeekdayOffset; i++) {
      elements.push(<li className="visible-hidden" key={i} />);
    }
    return elements;
  };

  const calendarShift = renderCalendarShift();
  const calendarDays = renderDaysInMonth();

  return (
    <ul className="date">
      {calendarShift}
      {calendarDays}
    </ul>
  );
};
